import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import ActionMenu from '../components/ActionMenu';
import { useNavigate } from 'react-router-dom';
import customerOrderService from '../services/customerOrderService';
import customerService from '../services/customerService';
import productService from '../services/productService';
import qboService from '../services/qboService';
import xeroService from '../services/xeroService';
import deliveryFleetService from '../services/deliveryFleetService';
import CustomizationDetails from '../components/CustomizationDetails';
import { useLanguage, t } from '../context/LanguageContext';
import { usePermissions } from '../context/PermissionContext';
import { useOrderNotifications } from '../context/OrderNotificationContext';
import LotCodeModal from '../components/LotCodeModal';
import PodViewModal from '../components/PodViewModal';
import DeliveryInfoModal from '../components/DeliveryInfoModal';
import PodCaptureModal from '../components/PodCaptureModal';
import PoScanReviewModal from '../components/PoScanReviewModal';
import SearchableSelect from '../components/SearchableSelect';
import ConfirmModal from '../components/ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_LABELS = {
  pending: { en: 'Pending', es: 'Pendiente', color: 'warning' },
  confirmed: { en: 'Confirmed', es: 'Confirmado', color: 'info' },
  in_production: { en: 'In Production', es: 'En Producción', color: 'primary' },
  ready: { en: 'Completed', es: 'Completado', color: 'success' },
  shipped: { en: 'Shipped', es: 'Enviado', color: 'secondary' },
  invoiced: { en: 'Invoiced', es: 'Facturado', color: 'dark' },
  cancelled: { en: 'Cancelled', es: 'Cancelado', color: 'danger' },
};

const DISPATCH_STATUS_LABELS = {
  offered: 'Dispatched — awaiting driver',
  accepted: 'Dispatched — accepted',
  picked_up: 'Dispatched — picked up',
  delivered: 'Dispatched — delivered',
};

const CHANNEL_LABELS = {
  retail: { en: 'Retail', es: 'Minorista' },
  food_service: { en: 'Food Service', es: 'Food Service' },
};

const INBOX_STATUSES = 'pending';
// Invoiced orders stay Active until explicitly archived (the invoice is often still being
// printed/handed over) — see the `archived` filter in fetchOrders.
const ACTIVE_STATUSES = 'confirmed,ready,shipped,invoiced';
const HISTORY_STATUSES = 'invoiced,cancelled';

function StatusBadge({ status, lang }) {
  const s = STATUS_LABELS[status] || { en: status, es: status, color: 'secondary' };
  return (
    <span className={`badge bg-${s.color}`}>
      {t(s.en, s.es, lang)}
    </span>
  );
}

function OrderRowItems({ items, lang }) {
  if (!items?.length) return <span className="text-muted">—</span>;
  return (
    <table className="table table-sm mb-0 mt-1" style={{ fontSize: '0.82rem' }}>
      <thead>
        <tr className="text-muted">
          <th className="fw-normal border-0 ps-0">{t('Product', 'Producto', lang)}</th>
          <th className="fw-normal border-0 text-end">{t('Qty', 'Cant', lang)}</th>
          <th className="fw-normal border-0 text-end">{t('Unit', 'Unidad', lang)}</th>
          <th className="fw-normal border-0 text-end">{t('U/cs', 'U/cj', lang)}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, i) => (
          <tr key={i}>
            <td className="ps-0 border-0">
              {item.presentationLabel || item.Product?.name || '—'}
              <CustomizationDetails item={item} />
            </td>
            <td className="text-end border-0 fw-semibold">{item.quantity}</td>
            <td className="text-end border-0 text-muted">
              {item.quantityUnit === 'units' ? t('units', 'unidades', lang) : t('cs', 'cj', lang)}
            </td>
            <td className="text-end border-0">
              {item.quantityUnit !== 'units' && item.unitsPerCase
                ? <span className="badge bg-secondary">{item.unitsPerCase}</span>
                : <span className="text-muted">—</span>
              }
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Searchable product picker used when no price list is configured
function ProductSearch({ products, value, onChange, lang }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = products.find(p => p.id === value);

  const filtered = useMemo(() => {
    if (!query) return products.slice(0, 30);
    const q = query.toLowerCase();
    return products.filter(p => p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q)).slice(0, 30);
  }, [products, query]);

  return (
    <div className="position-relative">
      <input
        className="form-control"
        placeholder={t('Search product...', 'Buscar producto...', lang)}
        value={open ? query : (selected ? selected.name : '')}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => setQuery(e.target.value)}
      />
      {open && filtered.length > 0 && (
        <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1050, maxHeight: 200, overflowY: 'auto' }}>
          {filtered.map(p => (
            <li key={p.id} className="list-group-item list-group-item-action py-1 small"
              onMouseDown={() => { onChange(p); setOpen(false); }}>
              {p.name}{p.sku ? <span className="text-muted ms-2">({p.sku})</span> : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// unitPriceSnapshot must always hold the price for one of whatever quantityUnit
// represents (case or individual unit) — every downstream total (invoices, POD,
// order emails) just does quantity * unitPriceSnapshot with no unit awareness.
const priceForQuantityUnit = (pricePerCase, unitsPerCase, unit) => {
  const ppc = Number(pricePerCase);
  if (!(ppc > 0)) return null;
  if (unit === 'units') {
    const upc = Number(unitsPerCase);
    return upc > 0 ? Math.round((ppc / upc) * 10000) / 10000 : ppc;
  }
  return ppc;
};

// Resolves the item's per-case reference price, falling back to the price list
// lookup for items loaded from an existing order (which only carry whatever
// unitPriceSnapshot was saved, not a separate case-price reference).
const resolvePricePerCase = (item, priceList) => {
  if (item.pricePerCase != null && item.pricePerCase !== '') return Number(item.pricePerCase);
  const found = priceList?.Items?.find(pi => String(pi.id) === String(item.priceListItemId));
  return found ? Number(found.pricePerCase) : null;
};

function NewOrderModal({ show, onClose, onSaved, customers, products, lang }) {
  const emptyItem = { productId: null, presentationLabel: '', quantity: '', quantityUnit: 'cases', unitsPerCase: '', unitPriceSnapshot: '', pricePerCase: '' };
  const empty = { customerId: '', requestedDate: '', orderChannel: '', poNumber: '', customerNotes: '', items: [{ ...emptyItem }] };
  const [form, setForm] = useState(empty);
  const [priceList, setPriceList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!show) { setForm(empty); setPriceList(null); setErr(''); }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPriceList = async (customerId) => {
    if (!customerId) { setPriceList(null); return; }
    try {
      const pl = await customerOrderService.getPriceListByCustomer(customerId);
      setPriceList(pl);
      if (pl?.Items?.length) {
        setForm(f => ({
          ...f,
          items: pl.Items.map(item => ({
            priceListItemId: item.id,
            presentationLabel: item.presentationLabel,
            productId: item.productId,
            unitsPerCase: item.unitsPerCase || '',
            unitPriceSnapshot: item.pricePerCase || '',
            quantity: '',
            quantityUnit: 'cases',
          })),
        }));
      }
    } catch {
      setPriceList(null);
    }
  };

  const handleCustomer = (customerId) => {
    const customer = customers.find(c => String(c.id) === String(customerId));
    setForm(f => ({
      ...f,
      customerId,
      orderChannel: customer?.defaultOrderChannel || '',
      items: [{ ...emptyItem }],
    }));
    loadPriceList(customerId);
  };

  const setItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  };

  // Switching between cases/units must also rescale the price snapshot —
  // unitPriceSnapshot always represents the price of ONE of whatever
  // quantityUnit currently is.
  const setQuantityUnit = (idx, unit) => {
    setForm(f => {
      const items = [...f.items];
      const item = items[idx];
      const pricePerCase = resolvePricePerCase(item, priceList);
      const unitPriceSnapshot = pricePerCase != null
        ? priceForQuantityUnit(pricePerCase, item.unitsPerCase, unit)
        : item.unitPriceSnapshot;
      items[idx] = { ...item, quantityUnit: unit, unitPriceSnapshot, pricePerCase: pricePerCase ?? item.pricePerCase };
      return { ...f, items };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.customerId) return setErr(t('Select a customer', 'Selecciona un cliente', lang));
    if (!form.requestedDate) return setErr(t('Requested date is required', 'La fecha es requerida', lang));
    if (!priceList && form.items.some(i => !i.productId))
      return setErr(t('Select a product for each item', 'Selecciona un producto para cada artículo', lang));
    if (form.items.some(i => !i.quantity || Number(i.quantity) <= 0))
      return setErr(t('All items need a quantity', 'Todos los artículos necesitan cantidad', lang));
    setSaving(true);
    try {
      await customerOrderService.createOrder({
        customerId: Number(form.customerId),
        requestedDate: form.requestedDate,
        orderChannel: form.orderChannel || null,
        poNumber: form.poNumber || null,
        customerNotes: form.customerNotes,
        items: form.items.map(i => ({
          priceListItemId: i.priceListItemId || null,
          productId: i.productId || null,
          presentationLabel: i.presentationLabel,
          unitsPerCase: i.unitsPerCase ? Number(i.unitsPerCase) : null,
          unitPriceSnapshot: i.unitPriceSnapshot ? Number(i.unitPriceSnapshot) : null,
          quantity: Number(i.quantity),
          quantityUnit: i.quantityUnit || 'cases',
        })),
      });
      onSaved();
      onClose();
    } catch {
      setErr(t('Failed to create order', 'Error al crear el pedido', lang));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('New Order', 'Nuevo Pedido', lang)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger py-2">{err}</div>}

        {/* Row 1: Customer + Date */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">{t('Customer', 'Cliente', lang)}</label>
            <SearchableSelect
              options={customers.map(c => ({ value: c.id, label: c.name }))}
              value={form.customerId}
              onChange={handleCustomer}
              placeholder={t('Select customer...', 'Seleccionar cliente...', lang)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">{t('Requested Date', 'Fecha Solicitada', lang)}</label>
            <input type="date" className="form-control" value={form.requestedDate}
              onChange={e => setForm(f => ({ ...f, requestedDate: e.target.value }))} />
          </div>
        </div>

        {/* Row 2: Channel + PO Number */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">{t('Channel', 'Canal', lang)}</label>
            <div className="btn-group d-flex" role="group">
              {['retail', 'food_service'].map(ch => (
                <button
                  key={ch}
                  type="button"
                  className={`btn btn-sm ${form.orderChannel === ch ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setForm(f => ({ ...f, orderChannel: f.orderChannel === ch ? '' : ch }))}
                >
                  {t(CHANNEL_LABELS[ch].en, CHANNEL_LABELS[ch].es, lang)}
                </button>
              ))}
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label">{t('PO Number', 'Número de OC', lang)} <span className="text-muted fw-normal small">({t('optional', 'opcional', lang)})</span></label>
            <input type="text" className="form-control" placeholder="e.g. PO-2026-001"
              value={form.poNumber}
              onChange={e => setForm(f => ({ ...f, poNumber: e.target.value }))} />
          </div>
        </div>

        {/* Items */}
        <label className="form-label fw-semibold">{t('Items', 'Artículos', lang)}</label>
        {form.items.map((item, idx) => (
          <div key={idx} className="border rounded p-2 mb-2 bg-light">
            <div className="row g-2 align-items-end">
              {/* Product selector */}
              <div className="col-md-6">
                {idx === 0 && <label className="form-label small text-muted mb-1">{t('Product', 'Producto', lang)}</label>}
                {priceList ? (
                  <select className="form-select form-select-sm" value={item.priceListItemId || ''}
                    onChange={e => {
                      const found = priceList.Items.find(i => String(i.id) === e.target.value);
                      setItem(idx, 'priceListItemId', e.target.value);
                      if (found) {
                        setItem(idx, 'presentationLabel', found.presentationLabel);
                        setItem(idx, 'productId', found.productId);
                        setItem(idx, 'unitsPerCase', found.unitsPerCase || '');
                        setItem(idx, 'pricePerCase', found.pricePerCase || '');
                        setItem(idx, 'unitPriceSnapshot', priceForQuantityUnit(found.pricePerCase, found.unitsPerCase, item.quantityUnit) ?? '');
                      }
                    }}>
                    <option value="">{t('Select SKU...', 'Seleccionar SKU...', lang)}</option>
                    {priceList.Items.map(i => <option key={i.id} value={i.id}>{i.presentationLabel}</option>)}
                  </select>
                ) : (
                  <ProductSearch products={products} value={item.productId} lang={lang}
                    onChange={p => { setItem(idx, 'productId', p.id); setItem(idx, 'presentationLabel', p.name); }} />
                )}
              </div>

              {/* Quantity + unit toggle */}
              <div className="col-md-3">
                {idx === 0 && <label className="form-label small text-muted mb-1">{t('Quantity', 'Cantidad', lang)}</label>}
                <div className="input-group input-group-sm">
                  <input type="number" className="form-control" placeholder="0"
                    value={item.quantity} min="0" step="1"
                    onChange={e => setItem(idx, 'quantity', e.target.value)} />
                  <button
                    type="button"
                    className={`btn btn-sm ${item.quantityUnit === 'cases' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    title={t('Cases', 'Cajas', lang)}
                    onClick={() => setQuantityUnit(idx, 'cases')}
                  >
                    {t('cs', 'cj', lang)}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${item.quantityUnit === 'units' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    title={t('Individual units', 'Unidades individuales', lang)}
                    onClick={() => setQuantityUnit(idx, 'units')}
                  >
                    {t('un', 'un', lang)}
                  </button>
                </div>
              </div>

              {/* Units/case (only relevant when ordering cases) */}
              <div className="col-md-2">
                {idx === 0 && <label className="form-label small text-muted mb-1">{t('Units/cs', 'Und/cj', lang)}</label>}
                <input type="number" className="form-control form-control-sm" placeholder="—"
                  value={item.unitsPerCase} disabled={item.quantityUnit === 'units'}
                  onChange={e => setItem(idx, 'unitsPerCase', e.target.value)} />
              </div>

              {/* Remove button */}
              <div className="col-md-1 d-flex align-items-end">
                {form.items.length > 1 && (
                  <button type="button" className="btn btn-outline-danger btn-sm w-100" onClick={() => removeItem(idx)}>×</button>
                )}
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={addItem}>
          + {t('Add Item', 'Agregar Artículo', lang)}
        </button>

        <div className="mt-3">
          <label className="form-label">{t('Notes', 'Notas', lang)}</label>
          <textarea className="form-control" rows={2} value={form.customerNotes}
            onChange={e => setForm(f => ({ ...f, customerNotes: e.target.value }))} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t('Cancel', 'Cancelar', lang)}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? t('Saving...', 'Guardando...', lang) : t('Create Order', 'Crear Pedido', lang)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function EventsModal({ orderId, orderNumber, show, onClose, lang }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !orderId) return;
    setLoading(true);
    customerOrderService.getOrderEvents(orderId)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [show, orderId]);

  const ACTION_LABELS = {
    created: { en: 'Order Created', es: 'Pedido Creado' },
    updated: { en: 'Order Updated', es: 'Pedido Actualizado' },
    confirmed: { en: 'Confirmed', es: 'Confirmado' },
    ready: { en: 'Marked Completed', es: 'Marcado Completado' },
    shipped: { en: 'Marked Shipped', es: 'Marcado Enviado' },
    invoiced: { en: 'Marked Invoiced', es: 'Marcado Facturado' },
    archived: { en: 'Archived', es: 'Archivado' },
    unarchived: { en: 'Restored to Active', es: 'Restaurado a Activos' },
    cancelled: { en: 'Cancelled', es: 'Cancelado' },
    acknowledged: { en: 'Acknowledged', es: 'Reconocido' },
    portal_submitted: { en: 'Submitted via Portal', es: 'Enviado por Portal' },
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          {t('Order History', 'Historial del Pedido', lang)}
          {orderNumber && <code className="text-muted ms-2 small">{orderNumber}</code>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
        ) : events.length === 0 ? (
          <p className="text-muted fst-italic mb-0">{t('No events recorded.', 'Sin eventos registrados.', lang)}</p>
        ) : (
          <ol className="list-group list-group-flush">
            {events.map(ev => {
              const lbl = ACTION_LABELS[ev.action] || { en: ev.action, es: ev.action };
              const dateStr = ev.createdAt ? new Date(ev.createdAt).toLocaleString() : '—';
              return (
                <li key={ev.id} className="list-group-item px-0 py-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <span className="fw-semibold">{t(lbl.en, lbl.es, lang)}</span>
                      {ev.userName && (
                        <span className="text-muted small ms-2">by {ev.userName}</span>
                      )}
                      {ev.note && (
                        <div className="text-muted small fst-italic mt-1">{ev.note}</div>
                      )}
                    </div>
                    <span className="text-muted small text-nowrap ms-3">{dateStr}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t('Close', 'Cerrar', lang)}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function EditOrderModal({ order, show, onClose, onSaved, products, lang }) {
  const emptyItem = { productId: null, presentationLabel: '', quantity: '', quantityUnit: 'cases', unitsPerCase: '', unitPriceSnapshot: '', pricePerCase: '' };
  const [form, setForm] = useState({ requestedDate: '', orderChannel: '', poNumber: '', customerNotes: '', notes: '', items: [{ ...emptyItem }] });
  const [priceList, setPriceList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!show || !order) return;
    setErr('');
    // Pre-fill form from existing order
    setForm({
      requestedDate: order.requestedDate || '',
      orderChannel: order.orderChannel || '',
      poNumber: order.poNumber || '',
      customerNotes: order.customerNotes || '',
      notes: order.notes || '',
      items: order.Items?.length
        ? order.Items.map(i => ({
            priceListItemId: i.priceListItemId || null,
            productId: i.productId || null,
            presentationLabel: i.presentationLabel || '',
            quantity: String(i.quantity),
            quantityUnit: i.quantityUnit || 'cases',
            unitsPerCase: i.unitsPerCase != null ? String(i.unitsPerCase) : '',
            unitPriceSnapshot: i.unitPriceSnapshot != null ? String(i.unitPriceSnapshot) : '',
          }))
        : [{ ...emptyItem }],
    });
    // Try to load price list for the customer
    if (order.customerId) {
      customerOrderService.getPriceListByCustomer(order.customerId)
        .then(pl => setPriceList(pl))
        .catch(() => setPriceList(null));
    }
  }, [show, order]); // eslint-disable-line react-hooks/exhaustive-deps

  const setItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  };

  // Switching between cases/units must also rescale the price snapshot —
  // unitPriceSnapshot always represents the price of ONE of whatever
  // quantityUnit currently is.
  const setQuantityUnit = (idx, unit) => {
    setForm(f => {
      const items = [...f.items];
      const item = items[idx];
      const pricePerCase = resolvePricePerCase(item, priceList);
      const unitPriceSnapshot = pricePerCase != null
        ? priceForQuantityUnit(pricePerCase, item.unitsPerCase, unit)
        : item.unitPriceSnapshot;
      items[idx] = { ...item, quantityUnit: unit, unitPriceSnapshot, pricePerCase: pricePerCase ?? item.pricePerCase };
      return { ...f, items };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (form.items.some(i => !i.quantity || Number(i.quantity) <= 0))
      return setErr(t('All items need a quantity', 'Todos los artículos necesitan cantidad', lang));
    setSaving(true);
    try {
      await customerOrderService.updateOrder(order.id, {
        requestedDate: form.requestedDate || null,
        orderChannel: form.orderChannel || null,
        poNumber: form.poNumber || null,
        customerNotes: form.customerNotes,
        notes: form.notes,
        items: form.items.map(i => ({
          priceListItemId: i.priceListItemId || null,
          productId: i.productId || null,
          presentationLabel: i.presentationLabel,
          unitsPerCase: i.unitsPerCase ? Number(i.unitsPerCase) : null,
          unitPriceSnapshot: i.unitPriceSnapshot ? Number(i.unitPriceSnapshot) : null,
          quantity: Number(i.quantity),
          quantityUnit: i.quantityUnit || 'cases',
        })),
      });
      onSaved();
      onClose();
    } catch {
      setErr(t('Failed to save changes', 'Error al guardar cambios', lang));
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;
  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('Edit Order', 'Editar Pedido', lang)}
          <code className="text-muted ms-2 small">{order.orderNumber}</code>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger py-2">{err}</div>}

        {/* Customer (read-only) */}
        <div className="mb-3">
          <label className="form-label">{t('Customer', 'Cliente', lang)}</label>
          <input className="form-control" value={order.Customer?.name || '—'} disabled />
        </div>

        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">{t('Requested Date', 'Fecha Solicitada', lang)}</label>
            <input type="date" className="form-control" value={form.requestedDate}
              onChange={e => setForm(f => ({ ...f, requestedDate: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('Channel', 'Canal', lang)}</label>
            <div className="btn-group d-flex" role="group">
              {['retail', 'food_service'].map(ch => (
                <button key={ch} type="button"
                  className={`btn btn-sm ${form.orderChannel === ch ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setForm(f => ({ ...f, orderChannel: f.orderChannel === ch ? '' : ch }))}>
                  {t(CHANNEL_LABELS[ch].en, CHANNEL_LABELS[ch].es, lang)}
                </button>
              ))}
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('PO Number', 'Número de OC', lang)}</label>
            <input type="text" className="form-control" placeholder="—"
              value={form.poNumber}
              onChange={e => setForm(f => ({ ...f, poNumber: e.target.value }))} />
          </div>
        </div>

        <label className="form-label fw-semibold">{t('Items', 'Artículos', lang)}</label>
        {form.items.map((item, idx) => (
          <div key={idx} className="border rounded p-2 mb-2 bg-light">
            <div className="row g-2 align-items-end">
              <div className="col-md-6">
                {idx === 0 && <label className="form-label small text-muted mb-1">{t('Product', 'Producto', lang)}</label>}
                {priceList?.Items?.length ? (
                  <select className="form-select form-select-sm" value={item.priceListItemId || ''}
                    onChange={e => {
                      const found = priceList.Items.find(i => String(i.id) === e.target.value);
                      setItem(idx, 'priceListItemId', e.target.value);
                      if (found) {
                        setItem(idx, 'presentationLabel', found.presentationLabel);
                        setItem(idx, 'productId', found.productId);
                        setItem(idx, 'unitsPerCase', found.unitsPerCase || '');
                        setItem(idx, 'pricePerCase', found.pricePerCase || '');
                        setItem(idx, 'unitPriceSnapshot', priceForQuantityUnit(found.pricePerCase, found.unitsPerCase, item.quantityUnit) ?? '');
                      }
                    }}>
                    <option value="">{t('Select SKU...', 'Seleccionar SKU...', lang)}</option>
                    {priceList.Items.map(i => <option key={i.id} value={i.id}>{i.presentationLabel}</option>)}
                  </select>
                ) : (
                  <div>
                    {item.presentationLabel
                      ? <span className="form-control form-control-sm bg-white">{item.presentationLabel}</span>
                      : <ProductSearch products={products} value={item.productId} lang={lang}
                          onChange={p => { setItem(idx, 'productId', p.id); setItem(idx, 'presentationLabel', p.name); }} />
                    }
                  </div>
                )}
              </div>
              <div className="col-md-3">
                {idx === 0 && <label className="form-label small text-muted mb-1">{t('Quantity', 'Cantidad', lang)}</label>}
                <div className="input-group input-group-sm">
                  <input type="number" className="form-control" placeholder="0"
                    value={item.quantity} min="0" step="1"
                    onChange={e => setItem(idx, 'quantity', e.target.value)} />
                  <button type="button"
                    className={`btn btn-sm ${item.quantityUnit === 'cases' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setQuantityUnit(idx, 'cases')}>{t('cs', 'cj', lang)}</button>
                  <button type="button"
                    className={`btn btn-sm ${item.quantityUnit === 'units' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setQuantityUnit(idx, 'units')}>{t('un', 'un', lang)}</button>
                </div>
              </div>
              <div className="col-md-2">
                {idx === 0 && <label className="form-label small text-muted mb-1">{t('Units/cs', 'Und/cj', lang)}</label>}
                <input type="number" className="form-control form-control-sm" placeholder="—"
                  value={item.unitsPerCase} disabled={item.quantityUnit === 'units'}
                  onChange={e => setItem(idx, 'unitsPerCase', e.target.value)} />
              </div>
              <div className="col-md-1 d-flex align-items-end">
                {form.items.length > 1 && (
                  <button type="button" className="btn btn-outline-danger btn-sm w-100" onClick={() => removeItem(idx)}>×</button>
                )}
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-outline-secondary btn-sm mt-1 mb-3" onClick={addItem}>
          + {t('Add Item', 'Agregar Artículo', lang)}
        </button>

        <div>
          <label className="form-label">{t('Notes', 'Notas', lang)}</label>
          <textarea className="form-control" rows={2} value={form.customerNotes}
            onChange={e => setForm(f => ({ ...f, customerNotes: e.target.value }))} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t('Cancel', 'Cancelar', lang)}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? t('Saving...', 'Guardando...', lang) : t('Save Changes', 'Guardar Cambios', lang)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── QA Lot Code Review Modal ──────────────────────────────────────────────────

function ReviewLotCodesModal({ orderId, show, onClose, onApproved, lang }) {
  const [data, setData]         = useState(null);
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!show || !orderId) return;
    setLoading(true);
    setError('');
    customerOrderService.getLotCodes(orderId)
      .then(res => {
        setData(res);
        setRows(res.lotCodes.map(lc => ({
          ...lc,
          lotCode:  lc.lotCode,
          quantity: String(lc.quantity),
          _key:     String(lc.id),
        })));
      })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [show, orderId]);

  const addRow = (item) => {
    setRows(prev => [...prev, {
      orderItemId: item.id,
      productId:   item.productId,
      lotCode:     '',
      quantity:    '',
      _key:        `new-${Date.now()}`,
    }]);
  };

  const updateRow = (key, field, value) =>
    setRows(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r));

  const removeRow = (key) =>
    setRows(prev => prev.filter(r => r._key !== key));

  const handleApprove = async () => {
    const filled = rows.filter(r => r.lotCode.trim() && r.quantity);
    if (!filled.length) { setError('Add at least one lot code before approving.'); return; }
    setApproving(true);
    setError('');
    try {
      const result = await customerOrderService.approveLotCodes(orderId, filled.map(r => ({
        orderItemId: r.orderItemId,
        productId:   r.productId,
        lotCode:     r.lotCode.trim(),
        quantity:    Number(r.quantity),
      })));
      onApproved(result);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setApproving(false);
    }
  };

  const allApproved = rows.length > 0 && rows.every(r => r.status === 'approved');

  const itemGroups = (data?.order?.Items || []).map(item => ({
    ...item,
    rows: rows.filter(r => r.orderItemId === item.id),
  }));

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Review Lot Codes — {data?.order?.orderNumber}
          {allApproved
            ? <span className="ms-2 badge bg-success" style={{ fontSize: 11 }}>✓ Approved</span>
            : <span className="ms-2 badge bg-warning text-dark" style={{ fontSize: 11 }}>QA Review</span>
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>{error}</div>}
        {loading && <div className="text-center py-4 text-muted"><span className="spinner-border spinner-border-sm me-2" />Loading…</div>}
        {data && !loading && (
          <>
            {allApproved && (
              <div className="alert alert-success py-2 px-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                <span>✅</span>
                <span>Lot codes have been approved and traceability records created. No further action needed.</span>
              </div>
            )}
            <div className="mb-3 small text-muted">
              Customer: <strong>{data.order.Customer?.name}</strong>
            </div>
            {itemGroups.map(item => (
              <div key={item.id} className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <strong style={{ fontSize: 13 }}>{item.Product?.name || '—'}</strong>
                  <span className="text-muted" style={{ fontSize: 12 }}>Ordered: {item.quantity}</span>
                </div>
                <table className="table table-sm mb-1" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th>Lot Code</th>
                      <th style={{ width: 120 }}>Qty (units)</th>
                      {!allApproved && <th style={{ width: 40 }}></th>}
                      {allApproved && <th style={{ width: 80 }}>Status</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {item.rows.map(row => (
                      <tr key={row._key}>
                        <td>
                          {allApproved
                            ? <span className="text-monospace">{row.lotCode}</span>
                            : <input className="form-control form-control-sm" value={row.lotCode}
                                onChange={e => updateRow(row._key, 'lotCode', e.target.value)} />
                          }
                        </td>
                        <td>
                          {allApproved
                            ? row.quantity
                            : <input className="form-control form-control-sm" type="number" min="0"
                                value={row.quantity}
                                onChange={e => updateRow(row._key, 'quantity', e.target.value)} />
                          }
                        </td>
                        {!allApproved && (
                          <td>
                            <button className="btn btn-outline-danger btn-sm px-2"
                              onClick={() => removeRow(row._key)}>✕</button>
                          </td>
                        )}
                        {allApproved && (
                          <td><span className="badge bg-success" style={{ fontSize: 10 }}>approved</span></td>
                        )}
                      </tr>
                    ))}
                    {item.rows.length === 0 && (
                      <tr><td colSpan={3} className="text-muted fst-italic" style={{ fontSize: 12 }}>No lot codes entered</td></tr>
                    )}
                  </tbody>
                </table>
                {!allApproved && (
                  <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 12 }}
                    onClick={() => addRow(item)}>
                    + Add lot code
                  </button>
                )}
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>{allApproved ? 'Close' : 'Cancel'}</Button>
        {!allApproved && (
          <Button variant="success" size="sm" onClick={handleApprove} disabled={approving || loading}>
            {approving
              ? <><span className="spinner-border spinner-border-sm me-1" />Creating Records…</>
              : '✅ Approve & Create Traceability Records'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ── QBO Invoice Modal ─────────────────────────────────────────────────────────

function QboInvoiceModal({ order, show, onClose, onInvoiced, lang }) {
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [creating, setCreating]   = useState(false);
  const [error, setError]         = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [result, setResult]       = useState(null);

  useEffect(() => {
    if (!show || !order) return;
    setLoading(true);
    setError('');
    setPreflight(null);
    setSendEmail(false);
    setResult(null);
    qboService.invoicePreflight(order.id)
      .then(setPreflight)
      .catch(e => setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to load invoice preview'))
      .finally(() => setLoading(false));
  }, [show, order]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const invoiceResult = await qboService.createInvoice(order.id, { sendEmail });
      setResult(invoiceResult);
      onInvoiced(invoiceResult);
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const handleDone = () => {
    setResult(null);
    onClose();
  };

  const customerEmail = preflight?.customerEmail;
  const canSendEmail  = preflight?.ready && !!customerEmail;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Create QBO Invoice — {order?.orderNumber}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>{error}</div>}
        {loading && <div className="text-center py-3 text-muted"><span className="spinner-border spinner-border-sm me-2" />Checking…</div>}

        {result && (
          <div>
            <div className="alert alert-success py-2 px-3 mb-2 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <span>✅</span>
              <span>Invoice <strong>#{result.invoiceNumber}</strong> created in QuickBooks.</span>
            </div>
            {sendEmail && (
              result.emailSent ? (
                <div className="alert alert-success py-2 px-3 mb-0 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                  <span>✅</span>
                  <span>Email sent to <strong>{customerEmail}</strong>.</span>
                </div>
              ) : (
                <div className="alert alert-danger py-2 px-3 mb-0" style={{ fontSize: 13 }}>
                  <div className="d-flex align-items-center gap-2">
                    <span>⚠️</span>
                    <span>Email was <strong>not</strong> sent.</span>
                  </div>
                  {result.emailError && (
                    <div className="mt-1" style={{ fontSize: 12 }}>{result.emailError}</div>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {!result && preflight && (
          <>
            <ul className="list-unstyled mb-0">
              {preflight.checks.map((c, i) => (
                <li key={i} className="d-flex flex-column mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: 16 }}>{c.ok ? '✅' : '⚠️'}</span>
                    <span style={{ fontSize: 13 }}>{c.label}</span>
                  </div>
                  {!c.ok && c.warning && (
                    <div className="text-danger ms-4" style={{ fontSize: 12 }}>{c.warning}</div>
                  )}
                  {c.info && (
                    <div className="text-warning ms-4" style={{ fontSize: 12 }}>{c.info}</div>
                  )}
                </li>
              ))}
            </ul>
            {preflight.ready && (
              <div className="alert alert-info py-2 px-3 mt-3 mb-0" style={{ fontSize: 12 }}>
                Ready to create invoice. Any items without a QBO code will be skipped.
              </div>
            )}
            {preflight.ready && (
              <div className="mt-3 p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="qbo-send-email"
                    checked={sendEmail}
                    disabled={!canSendEmail}
                    onChange={e => setSendEmail(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="qbo-send-email" style={{ fontSize: 13 }}>
                    Send invoice email to customer via QuickBooks
                  </label>
                </div>
                {sendEmail && customerEmail && (
                  <div className="mt-1 ms-4 text-muted" style={{ fontSize: 12 }}>
                    Will send to: <strong>{customerEmail}</strong>
                  </div>
                )}
                {!canSendEmail && (
                  <div className="mt-1 ms-4 text-muted" style={{ fontSize: 12 }}>
                    No email address on file for this customer.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <Button variant="primary" size="sm" onClick={handleDone}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreate}
              disabled={creating || loading || !preflight?.ready}
            >
              {creating
                ? <><span className="spinner-border spinner-border-sm me-1" />Creating…</>
                : sendEmail ? 'Create & Send Invoice' : 'Create Invoice'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ── Dispatch to Fleet Modal ─────────────────────────────────────────────────────

// Non-Shopify orders have no delivery address of their own — CustomerOrder.deliveryAddressLine1
// etc. are only ever populated by the Shopify webhook. Falls back to the wholesale Customer's own
// (single-string) address/phone as a starting point; always editable regardless of source.
function buildDispatchAddressForm(order) {
  const hasOwnAddress = !!order.deliveryAddressLine1;
  return {
    deliveryContactName: order.deliveryContactName || order.Customer?.name || '',
    deliveryPhone: order.deliveryPhone || order.Customer?.phone || '',
    deliveryAddressLine1: hasOwnAddress ? order.deliveryAddressLine1 : (order.Customer?.address || ''),
    deliveryAddressLine2: order.deliveryAddressLine2 || '',
    deliveryCity: order.deliveryCity || '',
    deliveryState: order.deliveryState || '',
    deliveryZip: order.deliveryZip || '',
  };
}

function DispatchFleetModal({ order, onClose, onDispatched, lang }) {
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState(() => buildDispatchAddressForm(order || {}));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (order) setAddress(buildDispatchAddressForm(order));
  }, [order]);

  if (!order) return null;

  const setField = (k, v) => setAddress(a => ({ ...a, [k]: v }));

  const handleDispatch = async () => {
    setSaving(true); setError('');
    try {
      await deliveryFleetService.dispatchOrder(order.id, {
        payoutAmountUsd: amount ? Number(amount) : undefined,
        ...address,
      });
      onDispatched();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to dispatch.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {t('Dispatch to Fleet', 'Despachar a la Flota', lang)} — {order.orderNumber}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

        <label className="form-label small mb-1">{t('Delivery Address', 'Dirección de Entrega', lang)}</label>
        <div className="row g-2 mb-3">
          <div className="col-6">
            <input className="form-control form-control-sm" placeholder={t('Contact name', 'Nombre de contacto', lang)}
              value={address.deliveryContactName} onChange={e => setField('deliveryContactName', e.target.value)} />
          </div>
          <div className="col-6">
            <input className="form-control form-control-sm" placeholder={t('Phone', 'Teléfono', lang)}
              value={address.deliveryPhone} onChange={e => setField('deliveryPhone', e.target.value)} />
          </div>
          <div className="col-12">
            <input className="form-control form-control-sm" placeholder={t('Address line 1', 'Dirección línea 1', lang)}
              value={address.deliveryAddressLine1} onChange={e => setField('deliveryAddressLine1', e.target.value)} />
          </div>
          <div className="col-12">
            <input className="form-control form-control-sm" placeholder={t('Address line 2 (optional)', 'Dirección línea 2 (opcional)', lang)}
              value={address.deliveryAddressLine2} onChange={e => setField('deliveryAddressLine2', e.target.value)} />
          </div>
          <div className="col-5">
            <input className="form-control form-control-sm" placeholder={t('City', 'Ciudad', lang)}
              value={address.deliveryCity} onChange={e => setField('deliveryCity', e.target.value)} />
          </div>
          <div className="col-4">
            <input className="form-control form-control-sm" placeholder={t('State', 'Estado', lang)}
              value={address.deliveryState} onChange={e => setField('deliveryState', e.target.value)} />
          </div>
          <div className="col-3">
            <input className="form-control form-control-sm" placeholder={t('Zip', 'Código Postal', lang)}
              value={address.deliveryZip} onChange={e => setField('deliveryZip', e.target.value)} />
          </div>
        </div>

        <label className="form-label small mb-1">{t('Driver Payout ($)', 'Pago al Conductor ($)', lang)}</label>
        <input
          type="number" min="0" step="0.01" className="form-control"
          placeholder={t('Leave blank to use company default', 'Dejar en blanco para usar el valor predeterminado', lang)}
          value={amount} onChange={e => setAmount(e.target.value)}
        />
        <p className="text-muted small mt-2 mb-0">
          {t('This will be pushed to every available driver — first to accept gets it.', 'Se enviará a todos los conductores disponibles — el primero en aceptar lo obtiene.', lang)}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>{t('Cancel', 'Cancelar', lang)}</Button>
        <Button variant="primary" size="sm" onClick={handleDispatch} disabled={saving}>
          {saving ? t('Dispatching…', 'Despachando…', lang) : t('Dispatch to Fleet', 'Despachar a la Flota', lang)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Xero Invoice Modal ────────────────────────────────────────────────────────

function XeroInvoiceModal({ order, show, onClose, onInvoiced, lang }) {
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [creating, setCreating]   = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (!show || !order) return;
    setLoading(true);
    setError('');
    setPreflight(null);
    xeroService.invoicePreflight(order.id)
      .then(setPreflight)
      .catch(e => setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to load invoice preview'))
      .finally(() => setLoading(false));
  }, [show, order]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await xeroService.createInvoice(order.id);
      onInvoiced();
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Create Xero Invoice — {order?.orderNumber}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>{error}</div>}
        {loading && <div className="text-center py-3 text-muted"><span className="spinner-border spinner-border-sm me-2" />Checking…</div>}
        {preflight && (
          <>
            <ul className="list-unstyled mb-0">
              {preflight.checks.map((c, i) => (
                <li key={i} className="d-flex flex-column mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: 16 }}>{c.ok ? '✅' : '⚠️'}</span>
                    <span style={{ fontSize: 13 }}>{c.label}</span>
                  </div>
                  {!c.ok && c.warning && (
                    <div className="text-danger ms-4" style={{ fontSize: 12 }}>{c.warning}</div>
                  )}
                  {c.info && (
                    <div className="text-warning ms-4" style={{ fontSize: 12 }}>{c.info}</div>
                  )}
                </li>
              ))}
            </ul>
            {preflight.ready && (
              <div className="alert alert-info py-2 px-3 mt-3 mb-0" style={{ fontSize: 12 }}>
                Ready to create invoice. Any items without a Xero code will be skipped.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleCreate}
          disabled={creating || loading || !preflight?.ready}
        >
          {creating ? <><span className="spinner-border spinner-border-sm me-1" />Creating…</> : 'Create Invoice'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Assign Delivery Modal ─────────────────────────────────────────────────────
function AssignDeliveryModal({ order, onClose, onSaved, lang }) {
  const [employees, setEmployees] = useState([]);
  const [empId,     setEmpId]     = useState(order.deliveryEmployeeId ? String(order.deliveryEmployeeId) : '');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/time-tracking/employees?status=active`, auth())
      .then(r => setEmployees(Array.isArray(r.data?.employees) ? r.data.employees : []))
      .catch(() => setError(t('Failed to load employees.', 'Error al cargar empleados.', lang)));
  }, [lang]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      await customerOrderService.assignDelivery(order.id, empId ? Number(empId) : null);
      onSaved();
      onClose();
    } catch {
      setError(t('Failed to save assignment.', 'Error al guardar asignación.', lang));
    } finally {
      setSaving(false);
    }
  };

  const opts = [
    { value: '', label: t('— Unassigned —', '— Sin asignar —', lang) },
    ...employees.map(e => ({ value: String(e.id), label: `${e.firstName} ${e.lastName}` })),
  ];

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {t('Assign Delivery', 'Asignar entrega', lang)} — #{order.orderNumber}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
          {t('Delivery Employee', 'Empleado de entrega', lang)}
        </label>
        <SearchableSelect
          options={opts}
          value={empId}
          onChange={setEmpId}
          placeholder={t('Select employee…', 'Seleccionar empleado…', lang)}
        />
        {error && <div className="text-danger mt-2" style={{ fontSize: 13 }}>{error}</div>}
        <p className="text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
          {t(
            'The assigned employee will see this order in their portal Deliveries tab. Once they capture a proof of delivery, it clears from their list.',
            'El empleado asignado verá este pedido en la pestaña Entregas del portal. Una vez que capture la prueba de entrega, desaparece de su lista.',
            lang
          )}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>{t('Cancel', 'Cancelar', lang)}</Button>
        <Button variant="primary" size="sm" onClick={save} disabled={saving}>
          {saving
            ? <><span className="spinner-border spinner-border-sm me-1" />{t('Saving…', 'Guardando…', lang)}</>
            : t('Save', 'Guardar', lang)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function SortableHeader({ column, label, sortBy, sortDir, onSort }) {
  const active = sortBy === column;
  return (
    <th
      role="button"
      onClick={() => onSort(column)}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
      title={`Sort by ${label}`}
    >
      {label}{' '}
      <span style={{ opacity: active ? 1 : 0.25, fontSize: 11 }}>
        {active ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
      </span>
    </th>
  );
}

function OrdersTable({ orders, onRefresh, isFactoryTeam, lang, products, canViewPrices, canQboInvoice, xeroStatus, canXeroInvoice, sortBy, sortDir, onSort }) {
  const navigate = useNavigate();
  const { hasModule } = usePermissions();
  const [transitioning, setTransitioning] = useState({});
  const [expanded, setExpanded] = useState({});
  const [eventsModal, setEventsModal] = useState(null); // { id, orderNumber }
  const [editModal, setEditModal] = useState(null); // full order object
  const [qboInvoiceOrder, setQboInvoiceOrder]   = useState(null);
  const [xeroInvoiceOrder, setXeroInvoiceOrder] = useState(null);
  const [lotCodeOrder, setLotCodeOrder]         = useState(null); // factory lot code entry
  const [reviewLotCodeId, setReviewLotCodeId] = useState(null); // QA review (order id)
  const [podViewOrder,       setPodViewOrder]       = useState(null);
  const [podCaptureOrder,    setPodCaptureOrder]    = useState(null);
  const [assignDeliveryOrder, setAssignDeliveryOrder] = useState(null);
  const [dispatchFleetOrder, setDispatchFleetOrder] = useState(null);
  const [deliveryInfoOrder, setDeliveryInfoOrder] = useState(null);
  const [sendingEmail, setSendingEmail]     = useState({}); // { [orderId]: true }
  const [emailSendError, setEmailSendError] = useState({}); // { [orderId]: message }
  const [resendConfirmOrder, setResendConfirmOrder] = useState(null); // order pending resend confirmation
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState(null); // order pending cancel confirmation

  const handleSendInvoiceEmail = async (ord) => {
    setSendingEmail(s => ({ ...s, [ord.id]: true }));
    setEmailSendError(e => ({ ...e, [ord.id]: null }));
    try {
      const result = await qboService.sendInvoiceEmail(ord.id);
      setEmailSendError(e => ({ ...e, [ord.id]: result.emailSent ? null : (result.emailError || 'Failed to send') }));
      onRefresh();
    } catch (e) {
      setEmailSendError(err => ({ ...err, [ord.id]: e.response?.data?.message || e.message || 'Failed to send' }));
    } finally {
      setSendingEmail(s => ({ ...s, [ord.id]: false }));
    }
  };

  const handleEmailIconClick = (ord) => {
    if (ord.qbInvoiceEmailSentAt) {
      setResendConfirmOrder(ord); // already sent — confirm before resending
    } else {
      handleSendInvoiceEmail(ord);
    }
  };

  // Archive / restore an invoiced order (moves it between the Active and History tabs).
  const doArchiveToggle = async (id, archive) => {
    setTransitioning(s => ({ ...s, [id]: true }));
    try {
      await (archive ? customerOrderService.archiveOrder(id) : customerOrderService.unarchiveOrder(id));
      onRefresh();
    } finally {
      setTransitioning(s => ({ ...s, [id]: false }));
    }
  };

  const doTransition = async (id, status) => {
    setTransitioning(s => ({ ...s, [id]: true }));
    try {
      await customerOrderService.transitionOrder(id, status);
      onRefresh();
    } finally {
      setTransitioning(s => ({ ...s, [id]: false }));
    }
  };

  const handleMarkReady = async (ord) => {
    setTransitioning(s => ({ ...s, [ord.id]: true }));
    try {
      await customerOrderService.transitionOrder(ord.id, 'ready');
      onRefresh();
      setLotCodeOrder(ord); // offer lot code entry after transition
    } finally {
      setTransitioning(s => ({ ...s, [ord.id]: false }));
    }
  };

  if (!orders.length) return (
    <p className="text-muted fst-italic">{t('No orders here.', 'No hay pedidos.', lang)}</p>
  );

  return (
    <>
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <SortableHeader column="orderNumber" label={t('Order #', 'Pedido #', lang)} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableHeader column="customerName" label={t('Customer', 'Cliente', lang)} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th>{t('Channel', 'Canal', lang)}</th>
              <SortableHeader column="status" label={t('Status', 'Estado', lang)} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableHeader column="createdAt" label={t('Received', 'Recibido', lang)} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableHeader column="requestedDate" label={t('Requested', 'Solicitado', lang)} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableHeader column="poNumber" label={t('PO #', 'OC #', lang)} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th>{t('Items', 'Artículos', lang)}</th>
              {canViewPrices && <th>{t('Total', 'Total', lang)}</th>}
              <th>{t('Notes', 'Notas', lang)}</th>
              <th>{t('Actions', 'Acciones', lang)}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(ord => (
              <tr
                key={ord.id}
                className={ord.status === 'cancelled' ? 'text-decoration-line-through' : ''}
                style={ord.status === 'cancelled' ? { opacity: 0.6 } : undefined}
              >
                <td>
                  <div className="d-flex align-items-center gap-1">
                    <code className="small">{ord.orderNumber}</code>
                    <button
                      className="btn btn-link btn-sm p-0 text-muted"
                      title={t('View history', 'Ver historial', lang)}
                      onClick={() => setEventsModal({ id: ord.id, orderNumber: ord.orderNumber })}
                    >
                      <small>🕐</small>
                    </button>
                  </div>
                  {ord.qbInvoiceNumber && (
                    <div style={{ fontSize: 11, marginTop: 2 }} className="d-flex align-items-center gap-1">
                      {ord.qbInvoiceUrl
                        ? <a href={ord.qbInvoiceUrl} target="_blank" rel="noreferrer"
                            className="text-success text-decoration-none"
                            title="Open in QuickBooks">
                            QB #{ord.qbInvoiceNumber} ↗
                          </a>
                        : <span className="text-success">QB #{ord.qbInvoiceNumber}</span>
                      }
                      {canQboInvoice && (
                        sendingEmail[ord.id] ? (
                          <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
                        ) : (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            style={{ fontSize: 12, lineHeight: 1, opacity: ord.qbInvoiceEmailSentAt ? 1 : 0.35 }}
                            disabled={!ord.Customer?.email}
                            title={
                              emailSendError[ord.id]
                                ? `Email failed: ${emailSendError[ord.id]}`
                                : ord.qbInvoiceEmailSentAt
                                  ? `Invoice emailed ${new Date(ord.qbInvoiceEmailSentAt).toLocaleString()} — click to resend`
                                  : ord.Customer?.email
                                    ? 'Send invoice email to customer'
                                    : 'No email on file for this customer'
                            }
                            onClick={() => handleEmailIconClick(ord)}
                          >
                            {emailSendError[ord.id] ? '⚠️' : '📧'}
                          </button>
                        )
                      )}
                    </div>
                  )}
                  {ord.xeroInvoiceNumber && (
                    <div style={{ fontSize: 11, marginTop: 2 }}>
                      {ord.xeroInvoiceUrl
                        ? <a href={ord.xeroInvoiceUrl} target="_blank" rel="noreferrer"
                            style={{ color: '#13b5ea', textDecoration: 'none' }}
                            title="Open in Xero">
                            Xero #{ord.xeroInvoiceNumber} ↗
                          </a>
                        : <span style={{ color: '#13b5ea' }}>Xero #{ord.xeroInvoiceNumber}</span>
                      }
                    </div>
                  )}
                  {ord.relayedFromOrderId && (
                    <div style={{ fontSize: 11, marginTop: 2 }} className="text-muted" title={ord.notes}>
                      ↩ Relayed from a reseller
                    </div>
                  )}
                  {ord.relayedToOrderId && (
                    <div style={{ fontSize: 11, marginTop: 2 }} className="text-muted">
                      ↪ Relayed to parent company
                    </div>
                  )}
                  {ord.Deliveries?.[0] && (
                    <div style={{ fontSize: 11, marginTop: 2 }} className="text-muted">
                      🚗 {DISPATCH_STATUS_LABELS[ord.Deliveries[0].status]}
                      {ord.Deliveries[0].Driver && <> — {ord.Deliveries[0].Driver.firstName} {ord.Deliveries[0].Driver.lastName}</>}
                      {ord.Deliveries[0].status === 'delivered' && (
                        ord.Deliveries[0].payoutStatus === 'paid'
                          ? <span className="text-success"> (Paid)</span>
                          : <span className="text-warning-emphasis"> (Payout pending)</span>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  {ord.eventId
                    ? <>{ord.Event?.name || `Event #${ord.eventId}`} <span className="badge bg-info text-dark ms-1" style={{ fontSize: 10 }}>Event</span></>
                    : ord.submittedVia === 'shopify'
                      ? (
                        <>
                          {ord.deliveryContactName || '—'} <span className="badge bg-success ms-1" style={{ fontSize: 10 }}>Shopify</span>{' '}
                          <button
                            className="btn btn-link btn-sm p-0 align-baseline"
                            title="Delivery info (contact, address, delivery method)"
                            onClick={() => setDeliveryInfoOrder(ord)}
                          >
                            <small>ℹ️</small>
                          </button>
                        </>
                      )
                      : (ord.Customer?.name || '—')}
                </td>
                <td>
                  {ord.orderChannel
                    ? <span className={`badge ${ord.orderChannel === 'retail' ? 'bg-info' : 'bg-warning text-dark'}`}>
                        {t(CHANNEL_LABELS[ord.orderChannel]?.en, CHANNEL_LABELS[ord.orderChannel]?.es, lang)}
                      </span>
                    : <span className="text-muted">—</span>}
                </td>
                <td><StatusBadge status={ord.status} lang={lang} /></td>
                <td className="small">{ord.createdAt ? new Date(ord.createdAt).toLocaleDateString() : '—'}</td>
                <td>{ord.requestedDate || '—'}</td>
                <td className="small">
                  {ord.poNumber
                    ? <code className="text-secondary">{ord.poNumber}</code>
                    : <span className="text-muted">—</span>}
                </td>
                <td style={{ minWidth: 200 }}>
                  <button className="btn btn-link btn-sm p-0" onClick={() => setExpanded(e => ({ ...e, [ord.id]: !e[ord.id] }))}>
                    {expanded[ord.id]
                      ? t('▲ Hide items', '▲ Ocultar', lang)
                      : `▼ ${ord.Items?.length || 0} ${t('items', 'artículos', lang)}`}
                  </button>
                  {expanded[ord.id] && <OrderRowItems items={ord.Items} lang={lang} />}
                </td>
                {canViewPrices && (
                  <td className="small fw-semibold text-end">
                    {(() => {
                      const total = (ord.Items || []).reduce((sum, i) => {
                        const qty = Number(i.quantity);
                        const price = Number(i.unitPriceSnapshot);
                        return sum + (qty > 0 && price > 0 ? qty * price : 0);
                      }, 0);
                      return total > 0
                        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total)
                        : <span className="text-muted fw-normal">—</span>;
                    })()}
                  </td>
                )}
                <td className="small text-muted" style={{ maxWidth: 160 }}>{ord.customerNotes || ord.notes || '—'}</td>
                <td>
                  {/* Pending orders only have three actions, so they stay as inline icon buttons —
                      quicker than opening a menu. Every later stage has many more, hence the menu. */}
                  {!isFactoryTeam && ord.status === 'pending' && (
                    <div className="d-flex gap-1 flex-nowrap">
                      <button className="btn btn-outline-secondary btn-sm px-2"
                        title={t('Edit order', 'Editar pedido', lang)}
                        onClick={() => setEditModal(ord)}>
                        ✏️
                      </button>
                      <button className="btn btn-success btn-sm px-2"
                        title={t('Confirm order', 'Confirmar pedido', lang)}
                        disabled={transitioning[ord.id]}
                        onClick={() => doTransition(ord.id, 'confirmed')}>
                        ✓
                      </button>
                      <button className="btn btn-outline-danger btn-sm px-2"
                        title={t('Delete order', 'Eliminar pedido', lang)}
                        disabled={transitioning[ord.id]}
                        onClick={() => setDeleteConfirmOrder(ord)}>
                        🗑️
                      </button>
                    </div>
                  )}
                  {!isFactoryTeam && ord.status !== 'pending' && (
                    <ActionMenu title={t('Actions', 'Acciones', lang)}>
                      {/* Edit */}
                      {ord.status === 'confirmed' && (
                        <ActionMenu.Item icon="✏️" onClick={() => setEditModal(ord)}>
                          {t('Edit Order', 'Editar Pedido', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* Create BOL */}
                      {['confirmed', 'ready', 'shipped'].includes(ord.status) && (
                        <ActionMenu.Item icon="🚚" onClick={() => navigate('/shipping/bol', {
                          state: {
                            fromOrder: {
                              id: ord.id,
                              orderNumber: ord.orderNumber,
                              poNumber: ord.poNumber,
                              qbInvoiceNumber: ord.qbInvoiceNumber,
                              xeroInvoiceNumber: ord.xeroInvoiceNumber,
                              customerId: ord.customerId,
                              customerName: ord.Customer?.name,
                              items: ord.Items,
                            },
                          },
                        })}>
                          {t('Create Bill of Lading', 'Crear Guía de Despacho', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* Xero Invoice — shown when Xero is the active provider */}
                      {xeroStatus?.connected && canXeroInvoice && (
                        ord.xeroInvoiceUrl
                          ? <ActionMenu.Item
                              icon={<span style={{ color: '#13b5ea', fontWeight: 700 }}>Xero ↗</span>}
                              href={ord.xeroInvoiceUrl} target="_blank" rel="noreferrer">
                              {t('Open Invoice', 'Ver Factura', lang)} #{ord.xeroInvoiceNumber}
                            </ActionMenu.Item>
                          : ord.xeroInvoiceNumber
                            ? <ActionMenu.Item disabled icon={<span style={{ fontWeight: 700 }}>Xero</span>}>
                                #{ord.xeroInvoiceNumber}
                              </ActionMenu.Item>
                            : ['confirmed', 'ready', 'shipped', 'invoiced'].includes(ord.status) && (
                                <ActionMenu.Item
                                  icon={<span style={{ color: '#13b5ea', fontWeight: 700 }}>Xero</span>}
                                  onClick={() => setXeroInvoiceOrder(ord)}>
                                  {t('Create Xero Invoice', 'Crear Factura Xero', lang)}
                                </ActionMenu.Item>
                              )
                      )}
                      {/* QB Invoice — shown when Xero is NOT the active provider */}
                      {!xeroStatus?.connected && canQboInvoice && (
                        ord.qbInvoiceUrl
                          ? <ActionMenu.Item
                              className="text-success"
                              icon={<span style={{ fontWeight: 700 }}>QB ↗</span>}
                              href={ord.qbInvoiceUrl} target="_blank" rel="noreferrer">
                              {t('Open Invoice', 'Ver Factura', lang)} #{ord.qbInvoiceNumber}
                            </ActionMenu.Item>
                          : ord.qbInvoiceNumber
                            ? <ActionMenu.Item disabled icon={<span style={{ fontWeight: 700 }}>QB</span>}>
                                #{ord.qbInvoiceNumber}
                              </ActionMenu.Item>
                            : ['confirmed', 'ready', 'shipped', 'invoiced'].includes(ord.status) && (
                                <ActionMenu.Item
                                  className="text-success"
                                  icon={<span style={{ fontWeight: 700 }}>QB</span>}
                                  onClick={() => setQboInvoiceOrder(ord)}>
                                  {t('Create QBO Invoice', 'Crear Factura QBO', lang)}
                                </ActionMenu.Item>
                              )
                      )}
                      {/* Primary status transition for this order's current state */}
                      {ord.status === 'confirmed' && (
                        <ActionMenu.Item icon="✅" disabled={transitioning[ord.id]} onClick={() => handleMarkReady(ord)}>
                          {t('Mark Ready / Enter Lot Codes', 'Marcar Listo / Ingresar Lotes', lang)}
                        </ActionMenu.Item>
                      )}
                      {ord.status === 'ready' && (
                        <ActionMenu.Item icon="📦" disabled={transitioning[ord.id]} onClick={() => doTransition(ord.id, 'shipped')}>
                          {t('Mark as Shipped', 'Marcar como Enviado', lang)}
                        </ActionMenu.Item>
                      )}
                      {ord.status === 'shipped' && (
                        <ActionMenu.Item icon="🧾" disabled={transitioning[ord.id]} onClick={() => doTransition(ord.id, 'invoiced')}>
                          {t('Mark as Invoiced', 'Marcar como Facturado', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* Invoiced orders stay Active until archived; archived ones can be restored.
                          Shopify orders are exempt from the invoiced-only gate — paid through
                          Shopify checkout up front, not this app's own invoicing workflow, so
                          Archive is available at any status instead of only after 5 transitions. */}
                      {(ord.status === 'invoiced' || ord.submittedVia === 'shopify') && !ord.archivedAt && (
                        <ActionMenu.Item icon="🗄️" disabled={transitioning[ord.id]} onClick={() => doArchiveToggle(ord.id, true)}>
                          {t('Archive Order', 'Archivar Pedido', lang)}
                        </ActionMenu.Item>
                      )}
                      {(ord.status === 'invoiced' || ord.submittedVia === 'shopify') && ord.archivedAt && (
                        <ActionMenu.Item icon="↩️" disabled={transitioning[ord.id]} onClick={() => doArchiveToggle(ord.id, false)}>
                          {t('Restore to Active', 'Restaurar a Activos', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* Lot codes — factory can add/edit, QA can review */}
                      {['ready', 'shipped', 'invoiced'].includes(ord.status) && (
                        ord.LotCodes?.some(lc => lc.status === 'pending_qa')
                          ? <ActionMenu.Item icon="🔬" className="text-warning-emphasis" onClick={() => setReviewLotCodeId(ord.id)}>
                              {t('Lot Codes Pending QA Review', 'Lotes Pendientes de Revisión QA', lang)}
                            </ActionMenu.Item>
                          : ord.LotCodes?.some(lc => lc.status === 'approved')
                            ? <ActionMenu.Item icon="🔬" className="text-success" onClick={() => setReviewLotCodeId(ord.id)}>
                                {t('Lot Codes Approved — Review', 'Lotes Aprobados — Revisar', lang)}
                              </ActionMenu.Item>
                            : <ActionMenu.Item icon="🔬" onClick={() => setLotCodeOrder(ord)}>
                                {t('Add Lot Codes', 'Agregar Lotes', lang)}
                              </ActionMenu.Item>
                      )}
                      {/* Delivery assignment — invoiced included, same reasoning as the POD-capture
                          gate below: invoicing is often required before an order ships, so an
                          order getting invoiced first shouldn't block assigning its delivery. */}
                      {['ready', 'shipped', 'invoiced'].includes(ord.status) && (
                        <ActionMenu.Item icon="👤" onClick={() => setAssignDeliveryOrder(ord)}>
                          {ord.DeliveryEmployee
                            ? t(`Assigned: ${ord.DeliveryEmployee.firstName} ${ord.DeliveryEmployee.lastName} — Change`, `Asignado: ${ord.DeliveryEmployee.firstName} ${ord.DeliveryEmployee.lastName} — Cambiar`, lang)
                            : t('Assign Delivery Employee', 'Asignar Empleado de Entrega', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* Dispatch to the delivery fleet — any order, not just direct-to-consumer
                          ones (originally Shopify-only; opened up to match drivers, which are
                          never restricted by how the order came in), and only if there's no
                          active (offered/accepted/picked_up) dispatch already. Note: orders
                          without a Shopify-populated delivery address (deliveryAddressLine1/
                          City/etc.) will dispatch with a blank address for the driver — there's
                          no fallback to the wholesale Customer's address yet. */}
                      {hasModule('deliveryfleet.dispatch')
                        && ['ready', 'shipped', 'invoiced'].includes(ord.status)
                        && !(ord.Deliveries && ord.Deliveries.length > 0) && (
                        <ActionMenu.Item icon="🚗" onClick={() => setDispatchFleetOrder(ord)}>
                          {t('Dispatch to Fleet', 'Despachar a la Flota', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* POD capture — shown for ready/shipped/invoiced with no POD yet. Invoiced is
                          included so an order that got invoiced before delivery isn't a dead end —
                          delivery (and the proof of it) can still happen after the invoice goes out. */}
                      {['ready', 'shipped', 'invoiced'].includes(ord.status) && (!ord.podStatus || ord.podStatus === 'none') && (
                        <ActionMenu.Item icon="🖊️" onClick={() => setPodCaptureOrder(ord)}>
                          {t('Capture Proof of Delivery', 'Capturar Prueba de Entrega', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* POD status — shown once a POD exists */}
                      {['ready', 'shipped', 'invoiced', 'cancelled'].includes(ord.status) && ord.podStatus && ord.podStatus !== 'none' && (
                        <ActionMenu.Item icon="📎" className={ord.podStatus === 'signed' ? 'text-success' : ''} onClick={() => setPodViewOrder(ord)}>
                          {ord.podStatus === 'signed'
                            ? t('View Proof of Delivery (Signed)', 'Ver Prueba de Entrega (Firmada)', lang)
                            : t('View Proof of Delivery (Photo)', 'Ver Prueba de Entrega (Foto)', lang)}
                        </ActionMenu.Item>
                      )}
                      {/* Delete — separated from every other action, always last */}
                      {ord.status === 'confirmed' && (
                        <>
                          <ActionMenu.Divider />
                          <ActionMenu.Item icon="🗑️" className="text-danger" disabled={transitioning[ord.id]} onClick={() => setDeleteConfirmOrder(ord)}>
                            {t('Delete Order', 'Eliminar Pedido', lang)}
                          </ActionMenu.Item>
                        </>
                      )}
                    </ActionMenu>
                  )}
                  {isFactoryTeam && (
                    <span className="text-muted small">{t('View only', 'Solo lectura', lang)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {eventsModal && (
        <EventsModal
          orderId={eventsModal.id}
          orderNumber={eventsModal.orderNumber}
          show={!!eventsModal}
          onClose={() => setEventsModal(null)}
          lang={lang}
        />
      )}

      <EditOrderModal
        order={editModal}
        show={!!editModal}
        onClose={() => setEditModal(null)}
        onSaved={() => { setEditModal(null); onRefresh(); }}
        products={products}
        lang={lang}
      />

      <ConfirmModal
        show={!!resendConfirmOrder}
        title={t('Resend Invoice Email?', '¿Reenviar Correo de Factura?', lang)}
        message={resendConfirmOrder && (
          <>
            {t('Email successfully sent on', 'Correo enviado exitosamente el', lang)}{' '}
            <strong>{new Date(resendConfirmOrder.qbInvoiceEmailSentAt).toLocaleString()}</strong>.
            <br />
            {t('Would you like to resend it?', '¿Deseas reenviarlo?', lang)}
          </>
        )}
        confirmLabel={t('Resend', 'Reenviar', lang)}
        cancelLabel={t('Cancel', 'Cancelar', lang)}
        variant="primary"
        onCancel={() => setResendConfirmOrder(null)}
        onConfirm={() => {
          const ord = resendConfirmOrder;
          setResendConfirmOrder(null);
          handleSendInvoiceEmail(ord);
        }}
      />

      <ConfirmModal
        show={!!deleteConfirmOrder}
        title={t('Delete Order?', '¿Eliminar Pedido?', lang)}
        message={deleteConfirmOrder && (
          <>
            {t('This will delete order', 'Esto eliminará el pedido', lang)}{' '}
            <code>{deleteConfirmOrder.orderNumber}</code>
            {deleteConfirmOrder.Customer?.name && (
              <> {t('for', 'de', lang)} <strong>{deleteConfirmOrder.Customer.name}</strong></>
            )}
            {deleteConfirmOrder.poNumber && (
              <> ({t('PO', 'PO', lang)} <code>{deleteConfirmOrder.poNumber}</code>)</>
            )}
            .
            <br />
            {t('This cannot be undone.', 'Esta acción no se puede deshacer.', lang)}
          </>
        )}
        confirmLabel={t('Delete Order', 'Eliminar Pedido', lang)}
        cancelLabel={t('Keep Order', 'Mantener Pedido', lang)}
        variant="danger"
        onCancel={() => setDeleteConfirmOrder(null)}
        onConfirm={() => {
          const ord = deleteConfirmOrder;
          setDeleteConfirmOrder(null);
          doTransition(ord.id, 'cancelled');
        }}
      />

      {qboInvoiceOrder && (
        <QboInvoiceModal
          order={qboInvoiceOrder}
          show={!!qboInvoiceOrder}
          onClose={() => setQboInvoiceOrder(null)}
          onInvoiced={() => onRefresh()}
          lang={lang}
        />
      )}

      {xeroInvoiceOrder && (
        <XeroInvoiceModal
          order={xeroInvoiceOrder}
          show={!!xeroInvoiceOrder}
          onClose={() => setXeroInvoiceOrder(null)}
          onInvoiced={() => onRefresh()}
          lang={lang}
        />
      )}

      {/* Factory lot code entry — opens automatically after marking ready */}
      {lotCodeOrder && (
        <LotCodeModal
          order={lotCodeOrder}
          show={!!lotCodeOrder}
          onClose={() => setLotCodeOrder(null)}
          onSaved={() => { onRefresh(); setLotCodeOrder(null); }}
          lang={lang}
        />
      )}

      {/* QA lot code review */}
      {reviewLotCodeId && (
        <ReviewLotCodesModal
          orderId={reviewLotCodeId}
          show={!!reviewLotCodeId}
          onClose={() => setReviewLotCodeId(null)}
          onApproved={() => { onRefresh(); setReviewLotCodeId(null); }}
          lang={lang}
        />
      )}

      {/* POD view modal */}
      {podViewOrder && (
        <PodViewModal
          orderId={podViewOrder.id}
          orderNumber={podViewOrder.orderNumber}
          onClose={() => setPodViewOrder(null)}
          onDeleted={() => onRefresh()}
        />
      )}

      {/* POD capture modal */}
      {podCaptureOrder && (
        <PodCaptureModal
          order={podCaptureOrder}
          onClose={() => setPodCaptureOrder(null)}
          onCaptured={() => { onRefresh(); }}
        />
      )}

      {/* Assign delivery modal */}
      {assignDeliveryOrder && (
        <AssignDeliveryModal
          order={assignDeliveryOrder}
          lang={lang}
          onClose={() => setAssignDeliveryOrder(null)}
          onSaved={() => { onRefresh(); }}
        />
      )}

      {/* Dispatch to fleet modal */}
      {dispatchFleetOrder && (
        <DispatchFleetModal
          order={dispatchFleetOrder}
          lang={lang}
          onClose={() => setDispatchFleetOrder(null)}
          onDispatched={() => { setDispatchFleetOrder(null); onRefresh(); }}
        />
      )}

      {/* Delivery info modal (Shopify orders — contact/address/delivery method) */}
      {deliveryInfoOrder && (
        <DeliveryInfoModal order={deliveryInfoOrder} onClose={() => setDeliveryInfoOrder(null)} />
      )}
    </>
  );
}

export default function AdminOrdersPage() {
  const { lang } = useLanguage();
  const { role, permissions } = usePermissions();
  const { newOrdersSignal, markSeen } = useOrderNotifications();
  const isFactoryTeam = role === 'factory_team';
  // Prices are visible to admin/superadmin always; other roles need the orders.view_prices user grant.
  // We check permissions directly (not hasModule) to bypass the company-subscription level —
  // this is a sub-permission, not a top-level module requiring a separate subscription.
  const canViewPrices  = role === 'admin' || role === 'superadmin' || permissions.includes('orders.view_prices');
  const canQboInvoice  = role === 'admin' || role === 'superadmin' || permissions.includes('orders.qbo_invoice');
  const canXeroInvoice = role === 'admin' || role === 'superadmin' || permissions.includes('orders.xero_invoice');
  const isFirstSignal = useRef(true);

  const PAGE_SIZE = 30;

  const [tab, setTab] = useState('active');
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Default: most recently received/created first. requestedDate isn't reliable to sort/group by on
  // its own — a customer order placed with no requested date (common on orders customers just send
  // in, with no particular delivery date attached) sorts unpredictably against the rest since there's
  // nothing to compare; every order always has a real createdAt, so that's the dependable default.
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [inboxCount, setInboxCount] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [xeroStatus, setXeroStatus]     = useState({ connected: false });
  const [scanFile, setScanFile] = useState(null);
  const scanFileRef = useRef();
  const scanCameraRef = useRef();

  const statusParam = tab === 'inbox' ? INBOX_STATUSES : tab === 'active' ? ACTIVE_STATUSES : HISTORY_STATUSES;

  // Debounce free-text search — avoid firing a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchOrders = useCallback(async (pageToLoad, append) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const data = await customerOrderService.getOrders({
        status: statusParam,
        search: searchQuery || undefined,
        page: pageToLoad,
        limit: PAGE_SIZE,
        sortBy,
        sortDir,
        archived: tab === 'active' ? 'false' : tab === 'history' ? 'true' : undefined,
      });
      setOrders(prev => (append ? [...prev, ...data.orders] : data.orders));
      setPage(data.page);
      setTotalPages(data.totalPages);
      setTotalCount(data.total);
      if (statusParam === INBOX_STATUSES && !append) setInboxCount(data.total);
    } catch {
      if (!append) setOrders([]);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [tab, statusParam, searchQuery, sortBy, sortDir]);

  // Clicking a sortable column header: toggle direction if it's already the active column,
  // otherwise switch to that column with its natural default direction (soonest/lowest first).
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  // Always keep inbox count fresh (even when on other tabs)
  const fetchInboxCount = useCallback(async () => {
    try {
      const data = await customerOrderService.getOrders({ status: INBOX_STATUSES });
      setInboxCount(data.length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchOrders(1, false); }, [fetchOrders]);

  const handleLoadMore = () => fetchOrders(page + 1, true);

  // Refresh inbox count when on other tabs
  useEffect(() => {
    if (statusParam !== INBOX_STATUSES) fetchInboxCount();
  }, [statusParam, fetchInboxCount]);

  // Auto-refresh when new portal orders arrive
  useEffect(() => {
    if (isFirstSignal.current) { isFirstSignal.current = false; return; }
    fetchInboxCount();
    if (tab === 'inbox') fetchOrders(1, false);
  }, [newOrdersSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    customerService.getCustomers().then(setCustomers).catch(() => {});
    if (!isFactoryTeam) productService.getProducts({ excludeIntermediate: true }).then(setProducts).catch(() => {});
    if (!isFactoryTeam) xeroService.getStatus().then(setXeroStatus).catch(() => {});
  }, [isFactoryTeam]);

  const handleTabChange = (key) => {
    setTab(key);
    if (key === 'inbox') markSeen();
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">{t('Orders', 'Pedidos', lang)}</h4>
        {!isFactoryTeam && (
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary btn-sm" onClick={() => scanCameraRef.current.click()}>📷 Take Photo</button>
            <input
              ref={scanCameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) setScanFile(f);
                e.target.value = '';
              }}
            />
            <button className="btn btn-outline-primary btn-sm" onClick={() => scanFileRef.current.click()}>📎 Scan PO</button>
            <input
              ref={scanFileRef}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) setScanFile(f);
                e.target.value = '';
              }}
            />
            <button className="btn btn-primary btn-sm" onClick={() => setShowNewModal(true)}>
              + {t('New Order', 'Nuevo Pedido', lang)}
            </button>
          </div>
        )}
      </div>

      <div className="d-flex align-items-end flex-wrap gap-2 mb-3">
        <ul className="nav nav-tabs mb-0 flex-grow-1">
          {[
            { key: 'inbox', en: 'Inbox', es: 'Bandeja' },
            { key: 'active', en: 'Active', es: 'Activos' },
            { key: 'history', en: 'History', es: 'Historial' },
          ].map(tab_ => (
            <li key={tab_.key} className="nav-item">
              <button
                className={`nav-link${tab === tab_.key ? ' active' : ''}`}
                onClick={() => handleTabChange(tab_.key)}
              >
                {t(tab_.en, tab_.es, lang)}
                {tab_.key === 'inbox' && inboxCount > 0 && (
                  <span className="badge bg-danger ms-1">{inboxCount}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 280 }}
          placeholder={t('Search by customer, PO, or invoice #…', 'Buscar por cliente, PO o factura…', lang)}
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
      ) : (
        <>
          <OrdersTable
            orders={orders}
            onRefresh={() => fetchOrders(1, false)}
            isFactoryTeam={isFactoryTeam}
            lang={lang}
            products={products}
            canViewPrices={canViewPrices}
            canQboInvoice={canQboInvoice}
            xeroStatus={xeroStatus}
            canXeroInvoice={canXeroInvoice}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
          <div className="text-center text-muted mt-2 mb-4" style={{ fontSize: 13 }}>
            {t('Showing', 'Mostrando', lang)} {orders.length} {t('of', 'de', lang)} {totalCount}
          </div>
          {page < totalPages && (
            <div className="d-flex justify-content-center mb-4">
              <button
                className="btn btn-outline-primary btn-sm px-4"
                style={{ borderRadius: 20 }}
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('Loading…', 'Cargando…', lang)}</>
                  : t('Load 30 more', 'Cargar 30 más', lang)}
              </button>
            </div>
          )}
        </>
      )}

      {!isFactoryTeam && (
        <NewOrderModal
          show={showNewModal}
          onClose={() => setShowNewModal(false)}
          onSaved={() => fetchOrders(1, false)}
          customers={customers}
          products={products}
          lang={lang}
        />
      )}
      {scanFile && (
        <PoScanReviewModal
          file={scanFile}
          customers={customers}
          products={products}
          onClose={() => setScanFile(null)}
          onConfirmed={() => {
            setScanFile(null);
            fetchOrders(1, false);
          }}
        />
      )}
    </div>
  );
}
